/*
 * Impulsa3 - Delivery preferences for the cart drawer.
 *
 * Persists the shopper's choices as cart attributes via /cart/update.js, so the
 * values survive the drawer re-render (the drawer replaces .drawer__inner with
 * innerHTML on every cart change) and land on the order in the admin.
 *
 * Set IMPULSA3_DISABLE_PAST_SLOTS_ON_SAME_DAY to false to let shoppers pick a
 * slot that has already passed when "Entregar en el día" is on.
 */
const IMPULSA3_DISABLE_PAST_SLOTS_ON_SAME_DAY = true;

/*
 * Days the shop does not deliver on, as JS weekday numbers (0 = Sunday). Used to
 * pick the date the picker starts on: the next working day. Saturday counts as a
 * working day; add 6 here to prefill Monday instead. The Liquid snippet repeats
 * this rule for the no-JavaScript render, so keep the two in step.
 */
const IMPULSA3_NON_WORKING_WEEKDAYS = [0];

if (!customElements.get('impulsa3-delivery-slot')) {
  class Impulsa3DeliverySlot extends HTMLElement {
    connectedCallback() {
      this.slotAttribute = this.dataset.slotAttribute;
      this.sameDayAttribute = this.dataset.samedayAttribute;
      this.sameDayValue = this.dataset.samedayValue || 'Sí';
      this.cutoffHour = parseFloat(this.dataset.samedayCutoffHour || '12');
      this.sameDayEnabled = this.dataset.samedayEnabled !== 'false';
      this.dateAttribute = this.dataset.dateAttribute;
      this.substituteAttribute = this.dataset.substituteAttribute;
      this.substituteValue = this.dataset.substituteValue || 'Sí';
      this.substituteOffValue = this.dataset.substituteOffValue || 'No';
      this.substituteIsStored = this.dataset.substituteStored === 'true';
      this.maxDays = parseInt(this.dataset.maxDays || '15', 10);
      this.cartIsEmpty = this.dataset.cartEmpty === 'true';
      // Already on the cart, so the prefill has nothing to write.
      this.dateIsStored = this.dataset.dateStored === 'true';
      this.timezone = this.dataset.timezone || '';

      this.radios = Array.from(this.querySelectorAll('.impulsa3-delivery-slot__radio'));
      this.sameDayWrapper = this.querySelector('.impulsa3-delivery-slot__sameday');
      this.sameDayInput = this.querySelector('#Impulsa3DeliverySameDay');
      this.substituteInput = this.querySelector('#Impulsa3SubstituteOutOfStock');
      this.dateInput = this.querySelector('.impulsa3-delivery-slot__date');
      this.nonWorkingWarning = this.querySelector('.impulsa3-delivery-slot__date-warning');
      this.errorMessage = this.querySelector('.impulsa3-delivery-slot__error');

      if (!this.bound) {
        this.radios.forEach((radio) => radio.addEventListener('change', this.onSlotChange.bind(this)));

        if (this.sameDayInput) {
          this.sameDayInput.addEventListener('change', this.onSameDayChange.bind(this));
        }

        if (this.substituteInput) {
          this.substituteInput.addEventListener('change', this.onSubstituteChange.bind(this));
        }

        if (this.dateInput) {
          this.dateInput.addEventListener('change', this.onDateChange.bind(this));
        }

        this.bound = true;
      }

      // Availability first: it can switch same-day off (past the cutoff, or on a
      // day the shop does not deliver), and that decides the calendar's floor.
      this.refreshAvailability();
      this.refreshDateRange();
      this.applyDefaults();

      // A drawer left open across the cutoff would keep offering same-day, and
      // one left open across midnight would keep an out-of-range date range.
      this.timer = setInterval(() => {
        this.refreshAvailability();
        this.refreshDateRange();
      }, 60000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    /* Current hour in the store's timezone, as a decimal (10:30 -> 10.5). */
    getStoreHour() {
      const now = new Date();

      if (this.timezone) {
        try {
          const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: this.timezone,
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          }).formatToParts(now);

          const hour = parseInt(parts.find((part) => part.type === 'hour').value, 10);
          const minute = parseInt(parts.find((part) => part.type === 'minute').value, 10);

          if (!Number.isNaN(hour) && !Number.isNaN(minute)) return hour + minute / 60;
        } catch (error) {
          // Unsupported timezone: fall through to the device clock.
        }
      }

      return now.getHours() + now.getMinutes() / 60;
    }

    /* Today in the store's timezone, as the YYYY-MM-DD that <input type="date"> speaks. */
    getStoreToday() {
      const now = new Date();

      if (this.timezone) {
        try {
          // 'en-CA' is the locale whose short date format already is YYYY-MM-DD.
          return new Intl.DateTimeFormat('en-CA', {
            timeZone: this.timezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }).format(now);
        } catch (error) {
          // Unsupported timezone: fall through to the device clock.
        }
      }

      const offset = now.getTimezoneOffset() * 60000;
      return new Date(now.getTime() - offset).toISOString().slice(0, 10);
    }

    addDays(isoDate, days) {
      const date = new Date(`${isoDate}T00:00:00Z`);
      date.setUTCDate(date.getUTCDate() + days);
      return date.toISOString().slice(0, 10);
    }

    /*
     * Re-derives the pickable window. Liquid already rendered it, but a drawer
     * rendered yesterday and read today would offer a date that is now in the
     * past, and the floor moves when same-day is switched on.
     *
     * The floor is normally the next working day: today is off the table, and
     * so is any non-working day. Same-day is the one exception — it delivers
     * today, so while it is on the floor drops to today, otherwise the field
     * would sit below its own min and the browser would refuse to submit the
     * checkout form.
     */
    refreshDateRange() {
      if (!this.dateInput) return;

      const today = this.getStoreToday();
      const sameDayOn = Boolean(this.sameDayInput && this.sameDayInput.checked);
      const firstDay = sameDayOn ? today : this.getNextWorkingDay();
      const lastDay = this.addDays(today, this.maxDays);

      this.dateInput.min = firstDay;
      this.dateInput.max = lastDay;

      const chosen = this.dateInput.value;
      if (chosen && (chosen < firstDay || chosen > lastDay)) {
        this.dateInput.value = '';
        this.save({ [this.dateAttribute]: '' });
      }
    }

    isWorkingDay(isoDate) {
      const weekday = new Date(`${isoDate}T00:00:00Z`).getUTCDay();
      return !IMPULSA3_NON_WORKING_WEEKDAYS.includes(weekday);
    }

    /* First working day strictly after today. */
    getNextWorkingDay() {
      let candidate = this.getStoreToday();

      // Bounded by the picker's own window, so a misconfigured list of
      // non-working days can never spin here.
      for (let step = 0; step < this.maxDays; step += 1) {
        candidate = this.addDays(candidate, 1);
        if (this.isWorkingDay(candidate)) return candidate;
      }

      return candidate;
    }

    /*
     * <input type="date"> can bound a range but cannot grey out single weekdays,
     * so a shopper can still land on a Sunday from the calendar or by typing.
     * Move them forward to the next day the shop delivers on and say why.
     */
    correctNonWorkingDate() {
      if (!this.dateInput || !this.dateInput.value) return false;
      if (this.isWorkingDay(this.dateInput.value)) {
        this.setNonWorkingWarning(false);
        return false;
      }

      let corrected = this.dateInput.value;
      for (let step = 0; step < 7 && !this.isWorkingDay(corrected); step += 1) {
        corrected = this.addDays(corrected, 1);
      }

      if (corrected > this.dateInput.max) corrected = this.getNextWorkingDay();

      this.dateInput.value = corrected;
      this.setNonWorkingWarning(true);
      return true;
    }

    setNonWorkingWarning(isVisible) {
      if (this.nonWorkingWarning) this.nonWorkingWarning.hidden = !isVisible;
    }

    /*
     * Liquid prefills the next working day and the substitution toggle's default
     * so the shopper sees an answer, but a value that is only painted is not on
     * the cart. Write them, so the order carries what the shopper was shown even
     * if they never touch it. One request for both: two in flight would race
     * each other's hold on the checkout button.
     *
     * Only on connect: doing it on the timer would put back a date the shopper
     * deliberately cleared. An empty cart is skipped — there is no order to
     * annotate yet, and the drawer hides the footer anyway.
     */
    applyDefaults() {
      const attributes = {};

      if (this.dateInput) {
        // A date stored before the rules tightened can be a Sunday: fix it even
        // when there is nothing to prefill.
        const corrected = this.correctNonWorkingDate();

        if (!this.cartIsEmpty && (!this.dateIsStored || corrected)) {
          if (!this.dateInput.value) this.dateInput.value = this.getNextWorkingDay();
          this.dateIsStored = true;
          attributes[this.dateAttribute] = this.dateInput.value;
        }
      }

      if (this.substituteInput && !this.cartIsEmpty && !this.substituteIsStored) {
        this.substituteIsStored = true;
        attributes[this.substituteAttribute] = this.getSubstituteValue();
      }

      if (Object.keys(attributes).length) this.save(attributes);
    }

    /*
     * Shows the same-day toggle only before the cutoff, greys out slots that
     * can no longer be served today, and clears anything left stale.
     */
    refreshAvailability() {
      const storeHour = this.getStoreHour();
      // No same-day on a day the shop does not deliver on: the calendar refuses
      // those days, so offering one here would contradict it.
      const deliversToday = this.isWorkingDay(this.getStoreToday());
      const beforeCutoff = this.sameDayEnabled && deliversToday && storeHour < this.cutoffHour;

      if (this.sameDayWrapper) this.sameDayWrapper.hidden = !beforeCutoff;

      // The drawer may have been rendered before the cutoff and read after it.
      if (!beforeCutoff && this.sameDayInput && this.sameDayInput.checked) {
        this.sameDayInput.checked = false;
        this.save({ [this.sameDayAttribute]: '' });
      }

      const sameDayOn = Boolean(beforeCutoff && this.sameDayInput && this.sameDayInput.checked);
      let clearedSelection = false;

      this.radios.forEach((radio) => {
        const endHour = parseFloat(radio.dataset.endHour);
        const unavailable = Boolean(
          IMPULSA3_DISABLE_PAST_SLOTS_ON_SAME_DAY && sameDayOn && !Number.isNaN(endHour) && endHour <= storeHour
        );

        radio.disabled = unavailable;

        if (unavailable && radio.checked) {
          radio.checked = false;
          clearedSelection = true;
        }
      });

      if (clearedSelection) this.save({ [this.slotAttribute]: '' });
    }

    onSlotChange(event) {
      this.save({ [this.slotAttribute]: event.target.value });
    }

    /*
     * Same-day and a preferred date can contradict each other, and the merchant
     * would get an order asking for both. Keep the two in step: turning same-day
     * on overwrites the date with today, and picking another day turns same-day
     * off. Turning it off has to move the date too — today is below the floor
     * once same-day is gone, and leaving it there would block the checkout form.
     */
    onSameDayChange(event) {
      const isOn = event.target.checked;
      const attributes = { [this.sameDayAttribute]: isOn ? this.sameDayValue : '' };

      if (this.dateInput) {
        const today = this.getStoreToday();
        const wanted = isOn ? today : this.getNextWorkingDay();

        if (isOn ? this.dateInput.value !== wanted : this.dateInput.value <= today) {
          this.dateInput.value = wanted;
          attributes[this.dateAttribute] = wanted;
          this.setNonWorkingWarning(false);
        }
      }

      this.save(attributes).then(() => {
        this.refreshDateRange();
        this.refreshAvailability();
      });
    }

    onDateChange() {
      const corrected = this.correctNonWorkingDate();
      const chosen = this.dateInput.value;
      const attributes = { [this.dateAttribute]: chosen };

      const leavesToday = chosen && chosen !== this.getStoreToday();
      if (leavesToday && this.sameDayInput && this.sameDayInput.checked) {
        this.sameDayInput.checked = false;
        attributes[this.sameDayAttribute] = '';
      }

      this.save(attributes).then(() => {
        // A correction moved the floor's reason for being, so re-derive it.
        if (corrected) this.refreshDateRange();
        this.refreshAvailability();
      });
    }

    /*
     * Off is an explicit "No", never a blank: a blank reads as "never answered"
     * and the next render would put the theme editor default back.
     */
    getSubstituteValue() {
      return this.substituteInput.checked ? this.substituteValue : this.substituteOffValue;
    }

    onSubstituteChange() {
      this.substituteIsStored = true;
      this.save({ [this.substituteAttribute]: this.getSubstituteValue() });
    }

    /* Writes cart attributes and holds the checkout button until it lands. */
    save(attributes) {
      this.setBusy(true);
      if (this.errorMessage) this.errorMessage.hidden = true;

      const cartUpdateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update';

      return fetch(`${cartUpdateUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Cart update failed with ${response.status}`);
          return response.json();
        })
        .catch((error) => {
          console.error('[impulsa3-delivery-slot]', error);
          if (this.errorMessage) this.errorMessage.hidden = false;
        })
        .finally(() => this.setBusy(false));
    }

    setBusy(isBusy) {
      this.classList.toggle('impulsa3-delivery-slot--loading', isBusy);

      const checkoutButton = document.getElementById('CartDrawer-Checkout');
      if (!checkoutButton) return;

      // Never re-enable a checkout button that is disabled for its own reason
      // (an empty cart), so only restore what we disabled ourselves.
      if (isBusy) {
        if (checkoutButton.disabled) return;
        checkoutButton.disabled = true;
        checkoutButton.dataset.impulsa3Locked = 'true';
      } else if (checkoutButton.dataset.impulsa3Locked === 'true') {
        checkoutButton.disabled = false;
        delete checkoutButton.dataset.impulsa3Locked;
      }
    }
  }

  customElements.define('impulsa3-delivery-slot', Impulsa3DeliverySlot);
}
